import { splitShipmentDiscountSchema } from "@redotech/redo-fulfillment-settings/split-shipment-discount";
import { posExternalLocationMappingSchema } from "@redotech/redo-return-core-common/pos-external-location";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { createPrefixedSchema } from "../schema-utils";
import type { ReturnsFlatTeamSettingKey } from "./catalog";
import {
  RETURNS_AGENT_REVIEW_ACTION_OPTIONS,
  RETURNS_AGENT_REVIEW_RISK_LEVEL_OPTIONS,
  RETURNS_BARCODE_TEXT_OPTIONS,
  RETURNS_CLOSE_SHOPIFY_RETURN_OPTIONS,
  RETURNS_COMPENSATION_METHOD_OPTIONS,
  RETURNS_EXCESS_EXCHANGE_VALUE_OPTIONS,
  RETURNS_EXCHANGE_CREATE_TRIGGER_OPTIONS,
  RETURNS_EXCHANGE_FINANCIAL_STRATEGY_OPTIONS,
  RETURNS_EXCHANGE_ORDER_LINE_ITEM_PRICE_SOURCE_OPTIONS,
  RETURNS_EXCHANGE_ORDER_PREFIX_TYPE_OPTIONS,
  RETURNS_GIFT_CARD_RECIPIENT_OPTIONS,
  RETURNS_LABEL_EXPIRATION_STRATEGY_OPTIONS,
  RETURNS_MERCHANT_CREATED_RETURN_LABEL_CHARGE_DEFAULT_OPTIONS,
  RETURNS_NON_ZERO_VALUE_EXCHANGE_STRATEGY_OPTIONS,
  RETURNS_OUT_OF_STOCK_ADVANCED_OPTIONS,
  RETURNS_PROCESS_AT_STATUS_OPTIONS,
  RETURNS_REFUND_DUTIES_MODE_OPTIONS,
  RETURNS_RESTOCK_OPTIONS,
  RETURNS_RETURN_TYPE_OPTIONS,
  RETURNS_STORE_CREDIT_TYPE_OPTIONS,
  returnsSettingOptionValues,
} from "./selection-options";

const BarcodeText = createEnum(
  returnsSettingOptionValues(RETURNS_BARCODE_TEXT_OPTIONS),
);
const AgentReviewAction = createEnum(
  returnsSettingOptionValues(RETURNS_AGENT_REVIEW_ACTION_OPTIONS),
);
const AgentReviewRiskLevel = createEnum(
  returnsSettingOptionValues(RETURNS_AGENT_REVIEW_RISK_LEVEL_OPTIONS),
);
const CloseShopifyReturnOption = createEnum(
  returnsSettingOptionValues(RETURNS_CLOSE_SHOPIFY_RETURN_OPTIONS),
);
const CompensationMethod = createEnum(
  returnsSettingOptionValues(RETURNS_COMPENSATION_METHOD_OPTIONS),
);
const GiftCardRecipient = createEnum(
  returnsSettingOptionValues(RETURNS_GIFT_CARD_RECIPIENT_OPTIONS),
);
const LabelExpirationStrategy = createEnum(
  returnsSettingOptionValues(RETURNS_LABEL_EXPIRATION_STRATEGY_OPTIONS),
);
const MerchantCreatedReturnLabelChargeDefault = createEnum(
  returnsSettingOptionValues(
    RETURNS_MERCHANT_CREATED_RETURN_LABEL_CHARGE_DEFAULT_OPTIONS,
  ),
);
const ProcessAtStatus = createEnum(
  returnsSettingOptionValues(RETURNS_PROCESS_AT_STATUS_OPTIONS),
);
const RefundDutiesMode = createEnum(
  returnsSettingOptionValues(RETURNS_REFUND_DUTIES_MODE_OPTIONS),
);
const RestockPolicy = createEnum(
  returnsSettingOptionValues(RETURNS_RESTOCK_OPTIONS),
);
const ReturnType = createEnum(
  returnsSettingOptionValues(RETURNS_RETURN_TYPE_OPTIONS),
);
const ExcessExchangeValue = createEnum(
  returnsSettingOptionValues(RETURNS_EXCESS_EXCHANGE_VALUE_OPTIONS),
);
const ExchangeCreateTrigger = createEnum(
  returnsSettingOptionValues(RETURNS_EXCHANGE_CREATE_TRIGGER_OPTIONS),
);
const ExchangeFinancialStrategy = createEnum(
  returnsSettingOptionValues(RETURNS_EXCHANGE_FINANCIAL_STRATEGY_OPTIONS),
);
const ExchangeOrderPrefixType = createEnum(
  returnsSettingOptionValues(RETURNS_EXCHANGE_ORDER_PREFIX_TYPE_OPTIONS),
);
const NonZeroValueExchangeStrategy = createEnum(
  returnsSettingOptionValues(RETURNS_NON_ZERO_VALUE_EXCHANGE_STRATEGY_OPTIONS),
);
const OutOfStockAdvanced = createEnum(
  returnsSettingOptionValues(RETURNS_OUT_OF_STOCK_ADVANCED_OPTIONS),
);
const StoreCreditType = createEnum(
  returnsSettingOptionValues(RETURNS_STORE_CREDIT_TYPE_OPTIONS),
);

const MetafieldDisplaysSchema = z
  .array(z.object({ key: z.string(), displayName: z.string().nullish() }))
  .nullish();

const AgentReviewRiskConfigsSchema = z
  .array(
    z.object({ riskLevel: AgentReviewRiskLevel, action: AgentReviewAction }),
  )
  .nullish();

const RegionMappingsSchema = z
  .array(z.object({ country: z.string(), locationIds: z.array(z.string()) }))
  .nullish();

const PosExternalLocationMappingsSchema = z
  .array(posExternalLocationMappingSchema)
  .nullish();

const LabelCustomTextSchema = z
  .object({ line1: z.string().nullish(), line2: z.string().nullish() })
  .nullish();

const ReturnsFlatSettingShape = {
  ...createPrefixedSchema("returns/ui", {
    defaultCollapsed: z.boolean().nullish(),
    aiReviewDefaultCollapsed: z.boolean().nullish(),
    lineItemDetailOptions: z.array(z.string()).nullish(),
    productMetafieldDisplays: MetafieldDisplaysSchema,
    variantMetafieldDisplays: MetafieldDisplaysSchema,
    exposeExternalId: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("returns/itemDisposition", {
    enabled: z.boolean().nullish(),
    gradeOptions: z.array(z.string()).nullish(),
    outcomeOptions: z.array(z.string()).nullish(),
  }).shape,
  ...createPrefixedSchema("returns/barcode", {
    enabled: z.boolean().nullish(),
    width: z.string().nullish(),
    height: z.string().nullish(),
    topDisplayOption: BarcodeText.nullish(),
    bottomDisplayOption: BarcodeText.nullish(),
  }).shape,
  ...createPrefixedSchema("returns/giftReceipts", {
    enabled: z.boolean().nullish(),
    missingBuyerInfoDescription: z.string().nullish(),
    emailNotifications: z.boolean().nullish(),
    notificationEmails: z.array(z.string()).nullish(),
    requireShopperEmailAndGiftInformation: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("returns/returnEditing", {
    allowCustomerCancelReturn: z.boolean().nullish(),
    allowCustomerEditExchangeAddress: z.boolean().nullish(),
    exchangeAddressLockedFields: z
      .array(z.enum(["country", "province", "city", "postalCode", "street"]))
      .nullish(),
  }).shape,
  ...createPrefixedSchema("returns/processing", {
    shopAgainAction: StoreCreditType.nullish(),
    riseTriggerUrl: z.string().nullish(),
    giftCardOrderProductId: z.string().nullish(),
    hideGiftCardBranding: z.boolean().nullish(),
    disableRiseCreditEmail: z.boolean().nullish(),
    allowRestockBeforeProcess: z.boolean().nullish(),
    checkForTryNowTags: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("returns/advancedProcessing", {
    compensationEnabled: z.boolean().nullish(),
    restockingEnabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("pos", {
    defaultPosRestock: z.boolean().nullish(),
    sendOnlyCompletionEmail: z.boolean().nullish(),
    allowPOSAdjustments: z.boolean().nullish(),
    allowPOSManualDiscounts: z.boolean().nullish(),
    externalLocationMappings: PosExternalLocationMappingsSchema,
  }).shape,
  ...createPrefixedSchema("returns/automation/returnProcessing", {
    exchange: ProcessAtStatus.nullish(),
    refund: ProcessAtStatus.nullish(),
    storeCredit: ProcessAtStatus.nullish(),
    exchangeDelay: z.coerce.number().min(0).nullish(),
    refundDelay: z.coerce.number().min(0).nullish(),
    storeCreditDelay: z.coerce.number().min(0).nullish(),
    reviewAfterWarehouseProcessing: z.boolean().nullish(),
    requireImageForUndeclaredItems: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("returns/automation/claimProcessing", {
    exchange: ProcessAtStatus.nullish(),
    refund: ProcessAtStatus.nullish(),
    storeCredit: ProcessAtStatus.nullish(),
    exchangeDelay: z.coerce.number().min(0).nullish(),
    refundDelay: z.coerce.number().min(0).nullish(),
    storeCreditDelay: z.coerce.number().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("returns/automation/merchantCreatedReturns", {
    enableMerchantReturnFlow: z.boolean().nullish(),
    enableBulkItemDetails: z.boolean().nullish(),
    merchantCreatedReturnLabelChargeDefault:
      MerchantCreatedReturnLabelChargeDefault.nullish(),
    merchantCreatedClaimsRequireShipping: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("returns/automation", {
    autoProcessGreenReturns: z.boolean().nullish(),
    autoProcessGreenReturnTypes: z.array(ReturnType).nullish(),
    autoProcessMerchantCreatedGreenReturnTypes: z.array(ReturnType).nullish(),
    dropoffAutoProcessCompensationMethods: z.array(z.unknown()).nullish(),
    waiveRefundStoreCreditBelowAmount: z.coerce.number().min(0).nullish(),
    addReturnTagToOriginalOrder: z.boolean().nullish(),
    defaultReimburseOriginalShipping: z.boolean().nullish(),
    returnAdjustmentArtifactId: z.string().nullish(),
    orderSyncArtifactId: z.string().nullish(),
    orderReturnInfoArtifactId: z.string().nullish(),
  }).shape,
  ...createPrefixedSchema("returns/automation/shopify", {
    createShopifyReturns: z.boolean().nullish(),
    createShopifyReturnsForClaimsAndWarranties: z.boolean().nullish(),
    createShopifyReturnTypes: z.array(CompensationMethod).nullish(),
    archiveShopifyOrdersAfterReturn: z.boolean().nullish(),
    closeShopifyReturnWhenProcessed: CloseShopifyReturnOption.nullish(),
    disableRefundLineItems: z.boolean().nullish(),
    shouldAutoProcessRedoReturnWhenShopifyReturnCloses: z.boolean().nullish(),
    storeCreditExpirationEnabled: z.boolean().nullish(),
    storeCreditExpirationDays: z.coerce.number().int().min(0).nullish(),
    giftCardRecipient: GiftCardRecipient.nullish(),
    disposeShopifyReverseFulfillmentOnRestock: z.boolean().nullish(),
    restockShopifyRefunds: z.boolean().nullish(),
    skipEcommerceProviderRestock: z.boolean().nullish(),
    applyRestockingFeeToShopifyReturns: z.boolean().nullish(),
    syncReturnFeesToShopify: z.boolean().nullish(),
    refundAllReturnedItems: z.boolean().nullish(),
    refundDutiesMode: RefundDutiesMode.nullish(),
    dutyDrawbackEnabled: z.boolean().nullish(),
    dutyDrawbackEligibleCountries: z.array(z.string()).nullish(),
    skipExchangeRefunds: z.boolean().nullish(),
    showStoreCreditOnRefundNote: z.boolean().nullish(),
    createSeparateShopifyReturnForGreenReturns: z.boolean().nullish(),
    createMultipleShopifyReturns: z.boolean().nullish(),
    addTrackingToShopifyReturns: z.boolean().nullish(),
    addOrderNotes: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("returns/exchanges/orderNotes", {
    disabled: z.boolean().nullish(),
    includeDate: z.boolean().nullish(),
    includeExistingNote: z.boolean().nullish(),
    addNoteToOriginalOrders: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("returns/exchanges/fulfillment", {
    ignoreInventory: z.boolean().nullish(),
    excessExchangeValue: ExcessExchangeValue.nullish(),
    outOfStockAdvanced: OutOfStockAdvanced.nullish(),
  }).shape,
  ...createPrefixedSchema("returns/exchanges/fulfillmentRegions", {
    enabled: z.boolean().nullish(),
    regionMappings: RegionMappingsSchema,
    defaultLocationIds: z.array(z.string()).nullish(),
  }).shape,
  ...createPrefixedSchema("returns/exchanges/orders", {
    createTrigger: ExchangeCreateTrigger.nullish(),
    labelWaitForInvoice: z.boolean().nullish(),
    shopifyExchangeOrderStrategy: z.string().nullish(),
    addAdditionalExchangeTags: z.boolean().nullish(),
    exchangeOrderCreationArtifactId: z.string().nullish(),
    shouldRemoveHoldTagWhenRelease: z.boolean().nullish(),
    separateQuantityForExchangeProducts: z.boolean().nullish(),
    applyFeesAsLineItem: z.boolean().nullish(),
    applyFeesAsLineItemName: z.string().nullish(),
    nonZeroValueExchangeStrategy: NonZeroValueExchangeStrategy.nullish(),
  }).shape,
  ...createPrefixedSchema("returns/exchanges/orderName", {
    reuseOrderName: z.boolean().nullish(),
    shouldStripHash: z.boolean().nullish(),
    prefix: z.string().nullish(),
    suffix: z.string().nullish(),
    returnType: ExchangeOrderPrefixType.nullish(),
    duplicateOrderNumberFormat: z.string().nullish(),
  }).shape,
  "returns/exchanges/exchangeShippingName": z.string().nullish(),
  "returns/exchanges/exchangeFinancialStrategy":
    ExchangeFinancialStrategy.nullish(),
  "returns/exchanges/exchangeOrderLineItemPriceSource": createEnum(
    returnsSettingOptionValues(
      RETURNS_EXCHANGE_ORDER_LINE_ITEM_PRICE_SOURCE_OPTIONS,
    ),
  ).nullish(),
  "returns/exchanges/addTaxToExchangeOrders": z.boolean().nullish(),
  ...createPrefixedSchema(
    "returns/exchanges/applyExchangeOrderPaymentGateways",
    {
      enabled: z.boolean().nullish(),
      upsellGateway: z.string().nullish(),
      returnValueGateway: z.string().nullish(),
    },
  ).shape,
  ...createPrefixedSchema("returns/permissions", {
    allowRefund: z.boolean().nullish(),
    allowStoreCredit: z.boolean().nullish(),
    allowExchange: z.boolean().nullish(),
    allowEditFeesAndBonuses: z.boolean().nullish(),
    allowEditGreenReturns: z.boolean().nullish(),
    allowEditReturn: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("returns/shipping", {
    useFixedPackageDimensions: z.boolean().nullish(),
    fixedPackageDimensions: z
      .object({
        length: z.number().nullish(),
        width: z.number().nullish(),
        height: z.number().nullish(),
      })
      .nullish(),
    skuGroupingEnabled: z.boolean().nullish(),
    skuGroupingCsvFileUrl: z.string().nullish(),
    onlyCombineLabelsFromSameFulfillment: z.boolean().nullish(),
    verifyAddressForLabel: z.boolean().nullish(),
    dropoffs: z.array(z.unknown()).nullish(),
    returnInStoreEnabled: z.boolean().nullish(),
    returnInStoreRadiusMiles: z.coerce.number().min(0).nullish(),
    inStoreLocations: z.array(z.unknown()).nullish(),
    customInStoreLocations: z.array(z.unknown()).nullish(),
    packingSlipEnabled: z.boolean().nullish(),
    includeExchangeItemsOnPackingSlip: z.boolean().nullish(),
    manualLabelPackingSlipEnabled: z.boolean().nullish(),
    inStoreDropOffPackingSlipEnabled: z.boolean().nullish(),
    deductLabelFromCredit: z.boolean().nullish(),
    deductLabelFromRefund: z.boolean().nullish(),
    deductLabelFromExchange: z.boolean().nullish(),
    customerLabelUpchargeFee: z.coerce.number().min(0).nullish(),
    dontChargeCustomerLabelUpchargeWhenOnlyOption: z.boolean().nullish(),
    excludeCustomerLabelFeeForExchanges: z.boolean().nullish(),
    includeMultipleCarrierOptions: z.boolean().nullish(),
    carrierChoiceMaxMerchantPaidAmount: z.coerce.number().min(0).nullish(),
    carrierChoiceMaxShownAmount: z.coerce.number().min(0).nullish(),
    merchantMaxLabelPrice: z.coerce.number().min(0).nullish(),
    pickupMaxCoveredAmountEnabled: z.boolean().nullish(),
    pickupMaxCoveredAmount: z.coerce.number().min(0).nullish(),
    labelIncludeReturnType: z.boolean().nullish(),
    labelRmaBarcodeEnabled: z.boolean().nullish(),
    packingInstructions: z.string().nullish(),
    formLabelInstructions: z.array(z.string()).nullish(),
    australiaPostPrinterlessEnabled: z.boolean().nullish(),
    australiaPostPrinterlessText: z.string().nullish(),
    requireReturnShippingSignatureEnabled: z.boolean().nullish(),
    requireReturnShippingSignatureThreshold: z.coerce.number().min(0).nullish(),
    requireReturnShippingSignatureForTagsEnabled: z.boolean().nullish(),
    requireReturnShippingSignatureForTagsTags: z.array(z.string()).nullish(),
    ignoreTeamCarrierOverrideInMerchantDash: z.boolean().nullish(),
  }).shape,
  "returns/shipping/labelCustomText": LabelCustomTextSchema,
  ...createPrefixedSchema("returns/withdrawals", {
    enabled: z.boolean().nullish(),
    shippingRefundMode: z.string().nullish(),
    shippingRefundFixedAmount: z.coerce.number().min(0).nullish(),
    tagsToAddToAutoCancelledOrders: z.array(z.string()).nullish(),
    enableMerchantWithdrawalSubmittedEmail: z.boolean().nullish(),
    merchantEmailsForNotifications: z.array(z.string()).nullish(),
    enablethirdPartyFulfillmentCancellationEmail: z.boolean().nullish(),
    thirdPartyFulfillmentCancellationEmailList: z.array(z.string()).nullish(),
    preventAutoCancellingUnfulfilledOrder: z.boolean().nullish(),
    hourDelayForAutoCancellation: z.coerce.number().min(0).nullish(),
    lateWithdrawalNoticeText: z.string().nullish(),
    tagsToBlockAutoCancellation: z.array(z.string()).nullish(),
    excludedCollections: z.array(z.string()).nullish(),
    excludedProductTags: z.array(z.string()).nullish(),
    excludedMetafields: z.array(z.unknown()).nullish(),
  }).shape,
  ...createPrefixedSchema("returns/warehouse", {
    gradeMappings: z
      .array(
        z.object({
          gradeName: z.string(),
          acceptance: z.string(),
          restock: z.boolean(),
        }),
      )
      .nullish(),
  }).shape,
  ...createPrefixedSchema("labelExpiration", {
    days: z.coerce.number().int().nullish(),
    strategy: LabelExpirationStrategy.nullish(),
    reminderEmails: z.boolean().nullish(),
    reminderEmailDays: z.array(z.number().int()).nullish(),
    compensationMethodsToRemind: z.array(CompensationMethod).nullish(),
    expirationEmails: z.boolean().nullish(),
    shouldReopenReturnWhenLabelUsed: z.boolean().nullish(),
    reopenReturnAfterDays: z.coerce.number().int().nullish(),
    flagReturnWhenReopened: z.boolean().nullish(),
  }).shape,
  "returns/automation/inventory/restock": RestockPolicy.nullish(),
  "returns/automation/agentReviewRiskConfigs": AgentReviewRiskConfigsSchema,
  "reFulfillment/splitShipmentDiscount": splitShipmentDiscountSchema.nullish(),
  "redoManagedReturns/gradeMappings": z.array(z.unknown()).nullish(),
  ...createPrefixedSchema("exchanges", {
    excessExchangeValue: ExcessExchangeValue.nullish(),
  }).shape,
  ...createPrefixedSchema("exchanges/leftOverValueShopperSelectionIncentive", {
    incentivizedMethod: z.enum(["refund", "store_credit"]).nullish(),
    value: z.number().min(0).nullish(),
    perCurrencyAmountsEnabled: z.boolean().nullish(),
    currencyOverrides: z.record(z.string(), z.number().min(0)).nullish(),
  }).shape,
} as const satisfies Record<ReturnsFlatTeamSettingKey, z.ZodTypeAny>;

export const ReturnsFlatTeamSettingsSchema = z.object(ReturnsFlatSettingShape);
